import React, { useState, useCallback } from 'react'

import styles from '../styles.module.scss'

// Webpack resolves the asset path; the project loads images through require().
// eslint-disable-next-line @typescript-eslint/no-var-requires
const arrowSvg = require('../../../../public/images/arrow.svg') as string

interface BlogPaginationProps {
  currentPage: number
  totalPages: number
  onPrev: () => void
  onNext: () => void
  onPageChange: (page: number) => void
}

const BlogPagination: React.FC<BlogPaginationProps> = ({
  currentPage,
  totalPages,
  onPrev,
  onNext,
  onPageChange,
}) => {
  const [inputValue, setInputValue] = useState(String(currentPage))

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '')
    setInputValue(val || '')
  }, [])

  const handleInputBlur = useCallback(() => {
    const num = parseInt(inputValue, 10)
    if (!isNaN(num) && num >= 1 && num <= totalPages) {
      onPageChange(num)
    } else {
      setInputValue(String(currentPage))
    }
  }, [inputValue, totalPages, currentPage, onPageChange])

  const handleInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur()
    }
  }, [])

  React.useEffect(() => {
    setInputValue(String(currentPage))
  }, [currentPage])

  const arrowMaskUrl = `url(${arrowSvg})`

  const ArrowIcon = ({ flip = false }: { flip?: boolean }) => (
    <span
      className={flip ? styles.paginationArrowIconLeft : styles.paginationArrowIcon}
      style={{
        maskImage: arrowMaskUrl,
        WebkitMaskImage: arrowMaskUrl,
      }}
      aria-hidden
    />
  )

  if (totalPages <= 1) return null

  return (
    <nav className={styles.pagination} aria-label="Blog pagination">
      <button
        type="button"
        className={styles.paginationArrow}
        onClick={onPrev}
        disabled={currentPage <= 1}
        aria-label="Previous page"
      >
        <ArrowIcon flip />
      </button>
      <span className={styles.paginationInfo}>
        <input
          type="text"
          inputMode="numeric"
          className={styles.paginationInput}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          aria-label="Current page"
        />
        {' of '}
        {totalPages}
      </span>
      <button
        type="button"
        className={styles.paginationArrow}
        onClick={onNext}
        disabled={currentPage >= totalPages}
        aria-label="Next page"
      >
        <ArrowIcon />
      </button>
    </nav>
  )
}

export default BlogPagination
